/**
 * useTranscriptionStream Hook
 *
 * Streams audio chunks to the backend for transcription via SSE.
 * Accumulates transcript text as it arrives.
 */

import { useState, useCallback, useRef } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { API_URL, getSessionToken } from '@/lib/api/client'
import type {
  TranscriptionEvent,
  TranscriptionTextEvent,
  TranscriptionErrorEvent,
  WhisperHealthResponse,
} from '@ispot/shared'

export interface UseTranscriptionStreamOptions {
  /** Callback when transcript text is received */
  onTranscript?: (text: string, isFinal: boolean, chunkIndex?: number) => void
  /** Callback when transcription error occurs */
  onError?: (error: Error) => void
  /** Language code (default: 'en') */
  language?: string
}

export interface UseTranscriptionStreamReturn {
  /** Send an audio chunk for transcription */
  sendChunk: (chunk: Blob, chunkIndex: number) => Promise<void>
  /** Check if whisper server is available */
  checkHealth: () => Promise<WhisperHealthResponse>
  /** Whether currently processing a chunk */
  isProcessing: boolean
  /** Accumulated full transcript */
  transcribedText: string
  /** Most recent chunk's text */
  lastChunkText: string
  /** Last error */
  error: Error | null
  /** Clear accumulated transcript */
  clearTranscript: () => void
}

/**
 * Convert a Blob to base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      // Remove data URL prefix (e.g., "data:audio/webm;base64,")
      const base64Data = base64.split(',')[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function useTranscriptionStream(
  options: UseTranscriptionStreamOptions = {}
): UseTranscriptionStreamReturn {
  const { onTranscript, onError, language = 'en' } = options
  const { accountId, projectId } = useSessionStore()

  // State
  const [isProcessing, setIsProcessing] = useState(false)
  const [transcribedText, setTranscribedText] = useState('')
  const [lastChunkText, setLastChunkText] = useState('')
  const [error, setError] = useState<Error | null>(null)

  // Ref to track all chunk texts in order
  const chunkTextsRef = useRef<Map<number, string>>(new Map())

  /**
   * Check if whisper server is available
   */
  const checkHealth = useCallback(async (): Promise<WhisperHealthResponse> => {
    const token = getSessionToken()
    const url = `${API_URL}/api/accounts/${accountId}/projects/${projectId}/transcription/health`

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()
      return data as WhisperHealthResponse
    } catch (err) {
      return {
        available: false,
        url: '',
        error: err instanceof Error ? err.message : 'Health check failed',
      }
    }
  }, [accountId, projectId])

  /**
   * Send an audio chunk for transcription
   */
  const sendChunk = useCallback(
    async (chunk: Blob, chunkIndex: number): Promise<void> => {
      if (!accountId || !projectId) {
        const err = new Error('No project context')
        setError(err)
        onError?.(err)
        return
      }

      setIsProcessing(true)
      setError(null)

      try {
        // Convert blob to base64
        const audioBase64 = await blobToBase64(chunk)

        const token = getSessionToken()
        const url = `${API_URL}/api/accounts/${accountId}/projects/${projectId}/transcription/chunk`

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            audio: audioBase64,
            mimeType: chunk.type,
            chunkIndex,
            language,
          }),
        })

        if (!response.ok) {
          throw new Error(`Transcription request failed: ${response.statusText}`)
        }

        // Parse SSE stream
        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response body')

        const decoder = new TextDecoder()
        let buffer = ''
        let currentEventType = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('event:')) {
              currentEventType = line.slice(6).trim()
              continue
            }
            if (line.startsWith('data:')) {
              try {
                const data = JSON.parse(line.slice(5).trim()) as TranscriptionEvent

                switch (currentEventType) {
                  case 'transcript': {
                    const textEvent = data as TranscriptionTextEvent
                    setLastChunkText(textEvent.text)

                    // Store chunk text
                    if (textEvent.chunkIndex !== undefined) {
                      chunkTextsRef.current.set(textEvent.chunkIndex, textEvent.text)
                    }

                    // Rebuild full transcript from all chunks in order
                    const sortedTexts = Array.from(chunkTextsRef.current.entries())
                      .sort((a, b) => a[0] - b[0])
                      .map(([, text]) => text)
                    const fullText = sortedTexts.join(' ')
                    setTranscribedText(fullText)

                    onTranscript?.(textEvent.text, textEvent.isFinal, textEvent.chunkIndex)
                    break
                  }

                  case 'error': {
                    const errorEvent = data as TranscriptionErrorEvent
                    const err = new Error(errorEvent.message)
                    setError(err)
                    onError?.(err)
                    break
                  }

                  case 'complete':
                    // Chunk processing complete
                    break
                }
              } catch {
                // Ignore parse errors
              }
            }
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Transcription failed')
        setError(error)
        onError?.(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [accountId, projectId, language, onTranscript, onError]
  )

  /**
   * Clear accumulated transcript
   */
  const clearTranscript = useCallback(() => {
    setTranscribedText('')
    setLastChunkText('')
    chunkTextsRef.current.clear()
    setError(null)
  }, [])

  return {
    sendChunk,
    checkHealth,
    isProcessing,
    transcribedText,
    lastChunkText,
    error,
    clearTranscript,
  }
}
